import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { PaymentService } from "../../services/paymentService";
import { API_URL } from "../../config/api";
import iconLogo from "../../assets/icon-logo.png";
import banner1 from "../../assets/banner-1.png";

interface TeamMember {
  id: number;
  user_id: number;
  member_position: string;
  full_name: string;
  personal_email: string;
  user?: {
    id: number;
    full_name: string;
    email: string;
  };
}

interface Team {
  id: number;
  team_id: string;
  team_name: string;
  league: string;
  category: string;
  registration_fee: number;
  formatted_fee: string;
  is_complete: boolean;
  payment_status: string;
  members_count: number;
  members?: TeamMember[];
  profile_status?: string;
  acknowledgment_status?: string;
  team_status?: string;
  is_payment_eligible?: boolean;
  payment_eligibility_message?: string;
}

export default function PaymentListPage() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paymentLoading, setPaymentLoading] = useState<{
    [key: number]: boolean;
  }>({});
  const [paymentStatus, setPaymentStatus] = useState<{ [key: number]: string }>(
    {},
  );
  const navigate = useNavigate();

  // Load user teams on component mount
  useEffect(() => {
    loadUserTeams();
  }, []);

  const loadUserTeams = async () => {
    setLoading(true);
    try {
      const response = await PaymentService.getUserTeams();
      if (response.success && response.data) {
        setTeams(response.data);
        setError("");
      } else {
        setError(response.message || "Failed to load teams");
      }
    } catch (err: any) {
      setError(err.message || "Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const handleTeamClick = (team: Team) => {
    // Don't allow clicking if team is not payment eligible
    if (!team.is_payment_eligible) {
      return; // Disable click for teams that are not eligible for payment
    }
    // Navigate to payment form with pre-selected team
    navigate(`/dashboard/payment/form?team=${team.team_id}`);
  };

  const handleVNPTPayment = async (team: Team) => {
    setPaymentLoading((prev) => ({ ...prev, [team.id]: true }));
    setPaymentStatus((prev) => ({ ...prev, [team.id]: "preparing" }));

    try {
      // Get user's basic info from team data
      const member = team.members?.[0];
      if (!member) {
        setError("Team member information is required for payment");
        return;
      }

      const paymentData = {
        team_id: team.id,
        amount: team.registration_fee,
        buyer_first_name: member.full_name.split(" ")[0] || "Member",
        buyer_last_name:
          member.full_name.split(" ").slice(1).join(" ") || "Name",
        buyer_phone: member.personal_email.includes("@")
          ? "0123456789"
          : member.personal_email, // Fallback phone
        buyer_email: member.personal_email,
        buyer_address: "Ho Chi Minh City",
        buyer_city: "Ho Chi Minh City",
        buyer_state: "Ho Chi Minh",
        buyer_country: "vn",
      };

      await PaymentService.processVNPTPayment(
        paymentData,
        (status) => {
          setPaymentStatus((prev) => ({ ...prev, [team.id]: status }));
        },
        (success, orderCode) => {
          setPaymentLoading((prev) => ({ ...prev, [team.id]: false }));
          if (success) {
            setPaymentStatus((prev) => ({ ...prev, [team.id]: "completed" }));
            // Reload teams to get updated payment status
            setTimeout(() => {
              loadUserTeams();
            }, 2000);
          } else {
            setPaymentStatus((prev) => ({ ...prev, [team.id]: "failed" }));
          }
        },
      );
    } catch (error: any) {
      console.error("Payment error:", error);
      setError(error.message || "Payment failed");
      setPaymentLoading((prev) => ({ ...prev, [team.id]: false }));
      setPaymentStatus((prev) => ({ ...prev, [team.id]: "failed" }));
    }
  };

  const getPaymentStatusText = (status: string) => {
    switch (status) {
      case "preparing":
      case "creating":
        return "Preparing payment...";
      case "redirecting":
        return "Opening payment gateway...";
      case "processing":
        return "Processing payment...";
      case "checking":
        return "Verifying payment...";
      case "completed":
        return "Payment completed!";
      case "failed":
        return "Payment failed";
      case "timeout":
        return "Payment timeout";
      default:
        return "Proceed to Payment";
    }
  };

  const [timeData, setTimeData] = useState<{ [key: string]: any }>({});
  const [currentTime, setCurrentTime] = useState(new Date());

  // Load time information for each team
  useEffect(() => {
    const loadTimeData = async () => {
      if (teams.length === 0) return;

      const timePromises = teams.map(async (team) => {
        try {
          const token = localStorage.getItem("token");

          if (
            team.payment_status === "completed" ||
            team.payment_status === "pending"
          ) {
            // Load contest access info for paid teams (completed or pending)
            const response = await fetch(
              `${API_URL.BASE_API}competition-phases/contest-access/${team.id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              },
            );
            if (response.ok) {
              const data = await response.json();
              return { teamId: team.id, data: data.data, type: "contest" };
            } else {
            }
          } else {
            // Load payment timeframe info for unpaid teams
            const response = await fetch(
              `${API_URL.BASE_API}payment/timeframe/${team.id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              },
            );
            if (response.ok) {
              const data = await response.json();
              return { teamId: team.id, data: data.data, type: "payment" };
            } else {
            }
          }
        } catch (error) {
          console.error(`Failed to load time data for team ${team.id}:`, error);
        }
        return null;
      });

      const results = await Promise.all(timePromises);
      const newTimeData: { [key: string]: any } = {};

      results.forEach((result) => {
        if (result) {
          newTimeData[result.teamId] = result;
        }
      });

      setTimeData(newTimeData);
    };

    loadTimeData();
  }, [teams]);

  // Update current time every minute for real-time countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const formatCountdown = (targetTime: string) => {
    const target = new Date(targetTime);
    const now = currentTime; // Use state for real-time updates
    const diff = target.getTime() - now.getTime();

    if (diff <= 0) {
      return "Time expired";
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return `${days} Day${days !== 1 ? "s" : ""} ${hours} Hour${
      hours !== 1 ? "s" : ""
    } ${minutes} Minute${minutes !== 1 ? "s" : ""}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
        {/* Main Content - Team List */}
        <div className="flex-1">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600">{error}</p>
              <button
                onClick={loadUserTeams}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Retry
              </button>
            </div>
          )}

          {teams.length === 0 && !error ? (
            <div className="text-center py-12">
              <img
                src={iconLogo}
                alt=""
                className="mx-auto h-16 w-16 mb-4 opacity-50"
              />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No teams found
              </h3>
              <p className="text-gray-600 mb-4">
                You need to create or join a team first.
              </p>
              <button
                onClick={() => navigate("/dashboard/registration")}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Go to Registration
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {teams.map((team) => {
                return (
                  <div
                    key={team.id}
                    className={`border-2 border-[#262423] rounded-lg p-4 sm:p-6 mb-4 shadow-sm transition-shadow ${
                      !team.is_payment_eligible
                        ? "cursor-not-allowed opacity-75"
                        : "hover:shadow-md cursor-pointer"
                    }`}
                    onClick={() => handleTeamClick(team)}
                  >
                    {/* Two Column Layout - Responsive */}
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-4">
                      {/* Left Side - Team Info - 40% on desktop */}
                      <div className="w-full sm:w-2/5 sm:pr-6 text-left flex flex-col min-h-[200px]">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                          {team.team_name}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500 font-mono mb-3 sm:mb-4 tracking-wider">
                          {team.team_id}
                        </p>
                        <hr className="border-gray-300 mb-3 sm:mb-4 w-full sm:w-1/2" />

                        {/* Team Members */}
                        <div className="space-y-2">
                          {/* Show actual member names if available */}
                          {team.members && team.members.length > 0 ? (
                            team.members.map((member, index) => (
                              <div
                                key={index}
                                className="flex items-center space-x-2 text-xs sm:text-sm text-gray-700"
                              >
                                <svg
                                  className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span className="truncate">
                                  {member.full_name}
                                </span>
                              </div>
                            ))
                          ) : (
                            <>
                              <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-700">
                                <svg
                                  className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span className="truncate">
                                  {team.league} - {team.category}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-700">
                                <svg
                                  className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                                </svg>
                                <span className="truncate">
                                  {team.members_count} members
                                </span>
                              </div>
                            </>
                          )}

                          {/* Show placeholder for missing members */}
                          {team.members_count < 2 && (
                            <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-400">
                              <svg
                                className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span className="truncate">
                                Waiting for team member...
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Payment Status - Moved here */}
                        <div className="mt-auto pt-4">
                          {(() => {
                            // Check if team is not payment eligible (needs approval first)
                            if (
                              !team.is_payment_eligible &&
                              team.payment_eligibility_message ===
                                "Team must be confirmed for approval first"
                            ) {
                              return (
                                <div className="space-y-1 min-h-[60px] flex flex-col justify-center">
                                  <div className="text-sm font-semibold text-orange-600">
                                    NEEDS APPROVAL
                                  </div>
                                  <div className="text-xs text-orange-500">
                                    {team.payment_eligibility_message}
                                  </div>
                                </div>
                              );
                            } else if (team.payment_status === "completed") {
                              return (
                                <div className="space-y-1 min-h-[60px] flex flex-col justify-center">
                                  <div className="text-sm font-semibold text-[#646464]">
                                    CONGRATULATIONS
                                  </div>
                                  <div className="text-xs text-green-600">
                                    Payment Completed
                                  </div>
                                </div>
                              );
                            } else if (team.payment_status === "pending") {
                              return (
                                <div className="space-y-1 min-h-[60px] flex flex-col justify-center">
                                  <div className="text-sm font-semibold text-[#646464]">
                                    CONGRATULATIONS
                                  </div>
                                  <div className="text-xs text-yellow-600">
                                    Waiting for confirmation from payment
                                    gateway
                                  </div>
                                </div>
                              );
                            } else {
                              const isLoading = paymentLoading[team.id];
                              const currentStatus = paymentStatus[team.id];

                              return (
                                <div className="space-y-3 min-h-[60px] flex flex-col justify-center">
                                  <div className="text-sm font-semibold text-[#646464]">
                                    AWAIT PAYMENT
                                  </div>
                                  <button
                                    className={`w-full px-3 py-2 rounded-md text-xs font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                      isLoading
                                        ? "bg-blue-600 text-white cursor-not-allowed"
                                        : "bg-gray-800 text-white hover:bg-gray-900 focus:ring-gray-500"
                                    }`}
                                    disabled={isLoading}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isLoading) {
                                        handleTeamClick(team);
                                      }
                                    }}
                                  >
                                    {isLoading ? (
                                      <div className="flex items-center justify-center space-x-2">
                                        <svg
                                          className="animate-spin h-3 w-3 text-white"
                                          xmlns="http://www.w3.org/2000/svg"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                        >
                                          <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                          ></circle>
                                          <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                          ></path>
                                        </svg>
                                        <span>
                                          {getPaymentStatusText(
                                            currentStatus || "preparing",
                                          )}
                                        </span>
                                      </div>
                                    ) : (
                                      getPaymentStatusText(currentStatus || "")
                                    )}
                                  </button>
                                </div>
                              );
                            }
                          })()}
                        </div>
                      </div>

                      {/* Right Side - Time Information - 60% on desktop */}
                      <div className="w-full sm:w-3/5 text-left">
                        {(() => {
                          const teamTimeData = timeData[team.id];

                          // Check if team is not payment eligible (needs approval first)
                          if (
                            !team.is_payment_eligible &&
                            team.payment_eligibility_message ===
                              "Team must be confirmed for approval first"
                          ) {
                            return (
                              <div className="bg-orange-50 rounded-lg p-6 min-h-[240px] flex items-center border border-orange-200">
                                <div className="text-center space-y-4 w-full">
                                  <div className="w-16 mx-auto">
                                    <img
                                      src={iconLogo}
                                      className="w-full opacity-60"
                                      alt="Logo"
                                    />
                                  </div>
                                  <div className="text-sm text-orange-700 font-medium">
                                    Complete Team Requirements
                                  </div>
                                  <div className="text-lg font-bold text-orange-900">
                                    {team.payment_eligibility_message}
                                  </div>
                                  <div className="text-xs text-orange-600 max-w-xs mx-auto">
                                    Please complete all team requirements and
                                    checklist items before payment becomes
                                    available. This includes having 2 confirmed
                                    members, signing terms & conditions, and
                                    submitting for approval.
                                  </div>
                                </div>
                              </div>
                            );
                          } else if (
                            team.payment_status === "completed" ||
                            team.payment_status === "pending"
                          ) {
                            // For paid teams (completed or pending): Show "You will be able to access Your Contest page in" + countdown to exam opening
                            return (
                              <div className="bg-white rounded-lg p-6 min-h-[240px] flex items-center">
                                <div className="text-center space-y-4 w-full">
                                  <div className="w-16 mx-auto">
                                    <img
                                      src={iconLogo}
                                      className="w-full"
                                      alt="Logo"
                                    />
                                  </div>
                                  <div className="text-sm text-blue-700">
                                    You will be able to access Your Competitions
                                    page in
                                  </div>
                                  <div className="text-2xl font-bold text-blue-900">
                                    {teamTimeData?.data?.countdown_target
                                      ? formatCountdown(
                                          teamTimeData.data.countdown_target,
                                        )
                                      : "Contest starts soon"}
                                  </div>
                                  {!teamTimeData && (
                                    <div className="text-xs text-blue-600">
                                      Contest timing information will be
                                      available soon
                                    </div>
                                  )}
                                  {teamTimeData?.data?.description && (
                                    <div className="text-xs text-blue-600">
                                      {teamTimeData.data.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          } else {
                            // For unpaid teams: Show name, description, amount, payment time, current or upcoming countdown
                            return (
                              <div className="bg-white rounded-lg p-6 text-[#646464] min-h-[240px] flex items-center">
                                <div className="text-center space-y-3 w-full">
                                  <div className="w-16 mx-auto">
                                    <img
                                      src={iconLogo}
                                      className="w-full"
                                      alt="Logo"
                                    />
                                  </div>

                                  {/* Name */}
                                  <div className="text-sm font-semibold">
                                    {teamTimeData?.data?.name ||
                                      "Payment Window"}
                                  </div>

                                  {/* Description */}
                                  <div className="text-xs">
                                    {teamTimeData?.data?.description ||
                                      "Complete your payment to secure your team's participation"}
                                  </div>

                                  {/* Amount */}
                                  {teamTimeData?.data?.formatted_amount && (
                                    <div className="text-sm font-semibold text-yellow-600">
                                      {teamTimeData.data.formatted_amount}
                                    </div>
                                  )}

                                  {/* Payment Time */}
                                  <div className="text-xs">
                                    Payment Period:{" "}
                                    {teamTimeData?.data?.start_time &&
                                    teamTimeData?.data?.end_time
                                      ? `${new Date(
                                          teamTimeData.data.start_time,
                                        ).toLocaleDateString()} - ${new Date(
                                          teamTimeData.data.end_time,
                                        ).toLocaleDateString()}`
                                      : "Available now"}
                                  </div>

                                  {/* Current or Upcoming Countdown */}
                                  <div>
                                    <div className="text-xs mb-1">
                                      {teamTimeData?.data?.countdown_label ||
                                        "Payment window status"}
                                    </div>
                                    <div className="text-lg font-bold text-yellow-900">
                                      {teamTimeData?.data?.countdown_target
                                        ? formatCountdown(
                                            teamTimeData.data.countdown_target,
                                          )
                                        : "Loading..."}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Sidebar - Contest Info & Tips */}
        <div className="lg:w-80 space-y-6">
          {/* Contest Countdown */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="text-center">
              <img
                src={banner1}
                alt=""
                className="w-full h-40 object-cover rounded-lg"
              />
            </div>
          </div>

          {/* Tips */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Tips for a better adventure
            </h3>
            <div className="text-xs text-gray-600 space-y-2 text-left">
              <p>Fields marked * are required.</p>
              <p>
                Team name must be unique and not duplicated in leagues and
                categories.
              </p>
              <p>
                Team name should start and end with the name of a constellation
                that you like.
              </p>
              <p>Do not use numbers and special characters for team name.</p>
              <p>
                Copy and share the team ID with your teammate, then wait for
                their confirmation to complete your 2-member team.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
